import type {
  Activity,
  ActivityStatus,
  ActivityType,
  CalendarInfo,
  ConstraintType,
  ParseProgress,
  Relationship,
  RelationshipType,
  ResourceAssignment,
  ResourceInfo,
  ScheduleModel,
  WbsNode,
} from '../types/schedule';
import { HARD_CONSTRAINTS } from '../types/schedule';

/**
 * Primavera P6 XER reader.
 *
 * An XER is a tab-delimited dump of the P6 tables. Records are prefixed:
 *   %T <table>   start of a table
 *   %F <cols…>   column headers for that table
 *   %R <vals…>   one row
 *   %E           end of file
 *
 * We read TASK, TASKPRED, TASKRSRC, PROJECT, PROJWBS, CALENDAR, RSRC and the
 * activity-code tables, then normalise into a ScheduleModel.
 */

type XerTables = Record<string, Record<string, string>[]>;

const TASK_TYPE_MAP: Record<string, ActivityType> = {
  TT_Task: 'Task',
  TT_Mile: 'Milestone',
  TT_FinMile: 'Finish Milestone',
  TT_LOE: 'Level of Effort',
  TT_WBS: 'WBS Summary',
  TT_Rsrc: 'Resource Dependent',
};

const STATUS_MAP: Record<string, ActivityStatus> = {
  TK_NotStart: 'Not Started',
  TK_Active: 'In Progress',
  TK_Complete: 'Completed',
};

const CONSTRAINT_MAP: Record<string, ConstraintType> = {
  CS_MSO: 'Start On',
  CS_MSOB: 'Start On or Before',
  CS_MSOA: 'Start On or After',
  CS_MEO: 'Finish On',
  CS_MEOB: 'Finish On or Before',
  CS_MEOA: 'Finish On or After',
  CS_MANDSTART: 'Mandatory Start',
  CS_MANDFIN: 'Mandatory Finish',
  CS_ALAP: 'As Late As Possible',
};

const RELATIONSHIP_MAP: Record<string, RelationshipType> = {
  PR_FS: 'FS',
  PR_SS: 'SS',
  PR_FF: 'FF',
  PR_SF: 'SF',
};

/**
 * XER dates are `YYYY-MM-DD HH:MM` (sometimes date-only). `new Date(str)` on
 * that form is implementation-defined, so parse the parts explicitly.
 */
export function parseXerDate(value: string | undefined | null): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!match) {
    const fallback = new Date(trimmed);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  const [, y, m, d, hh, mm, ss] = match;
  const date = new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    Number(hh ?? 0),
    Number(mm ?? 0),
    Number(ss ?? 0)
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

function num(value: string | undefined | null, fallback = 0): number {
  if (value === undefined || value === null || value.trim() === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Split an XER into its constituent tables without holding the file twice. */
function readTables(text: string): XerTables {
  const tables: XerTables = {};
  let current = '';
  let headers: string[] = [];

  for (const rawLine of text.split('\n')) {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
    if (line.startsWith('%T')) {
      current = line.split('\t')[1]?.trim() ?? '';
      headers = [];
      if (current) tables[current] = [];
    } else if (line.startsWith('%F')) {
      headers = line.split('\t').slice(1).map((h) => h.trim());
    } else if (line.startsWith('%R')) {
      if (!current || headers.length === 0) continue;
      const values = line.split('\t').slice(1);
      const row: Record<string, string> = {};
      for (let i = 0; i < headers.length; i++) {
        row[headers[i]] = (values[i] ?? '').trim();
      }
      tables[current].push(row);
    }
  }

  return tables;
}

function buildCalendars(tables: XerTables): Map<string, CalendarInfo> {
  const calendars = new Map<string, CalendarInfo>();
  for (const row of tables['CALENDAR'] ?? []) {
    // day_hr_cnt is occasionally blank on inherited calendars; 8 is P6's own
    // default and the only safe assumption at that point.
    const hoursPerDay = num(row.day_hr_cnt, 8);
    calendars.set(row.clndr_id, {
      id: row.clndr_id,
      name: row.clndr_name || 'Unnamed calendar',
      hoursPerDay: hoursPerDay > 0 ? hoursPerDay : 8,
    });
  }
  return calendars;
}

/** Activity codes arrive across three tables; flatten to name → value. */
function buildActivityCodes(tables: XerTables): {
  byTask: Map<string, Record<string, string>>;
  typeNames: string[];
} {
  const typeNames = new Map<string, string>();
  for (const row of tables['ACTVTYPE'] ?? []) {
    typeNames.set(row.actv_code_type_id, row.actv_code_type || 'Code');
  }

  const codeValues = new Map<string, { typeId: string; label: string }>();
  for (const row of tables['ACTVCODE'] ?? []) {
    codeValues.set(row.actv_code_id, {
      typeId: row.actv_code_type_id,
      label: row.actv_code_name || row.short_name || '',
    });
  }

  const byTask = new Map<string, Record<string, string>>();
  const usedTypes = new Set<string>();

  for (const row of tables['TASKACTV'] ?? []) {
    const code = codeValues.get(row.actv_code_id);
    if (!code) continue;
    const typeName = typeNames.get(code.typeId) ?? 'Code';
    usedTypes.add(typeName);

    const existing = byTask.get(row.task_id) ?? {};
    existing[typeName] = code.label;
    byTask.set(row.task_id, existing);
  }

  return { byTask, typeNames: [...usedTypes].sort() };
}

function buildRelationships(tables: XerTables): Relationship[] {
  const relationships: Relationship[] = [];
  for (const row of tables['TASKPRED'] ?? []) {
    if (!row.task_id || !row.pred_task_id) continue;
    relationships.push({
      predecessorId: row.pred_task_id,
      successorId: row.task_id,
      type: RELATIONSHIP_MAP[row.pred_type] ?? 'FS',
      lagHours: num(row.lag_hr_cnt),
      lagDays: 0, // filled once the calendar for the successor is known
    });
  }
  return relationships;
}

function buildResourceAssignments(
  tables: XerTables
): Map<string, ResourceAssignment[]> {
  const byTask = new Map<string, ResourceAssignment[]>();
  for (const row of tables['TASKRSRC'] ?? []) {
    const assignment: ResourceAssignment = {
      resourceId: row.rsrc_id,
      budgetUnits: num(row.target_qty),
      actualUnits: num(row.act_reg_qty) + num(row.act_ot_qty),
      remainingUnits: num(row.remain_qty),
      budgetCost: num(row.target_cost),
      actualCost: num(row.act_reg_cost) + num(row.act_ot_cost),
      remainingCost: num(row.remain_cost),
    };
    const list = byTask.get(row.task_id);
    if (list) list.push(assignment);
    else byTask.set(row.task_id, [assignment]);
  }
  return byTask;
}

/**
 * Pick the data date. `last_recalc_date` is the authoritative field; when a
 * file predates it or the export omitted PROJECT we fall back to the latest
 * actual date in the schedule, which is the best available proxy.
 */
function resolveDataDate(
  projectRow: Record<string, string> | undefined,
  activities: Activity[]
): { dataDate: Date | null; inferred: boolean } {
  const recalc = parseXerDate(projectRow?.last_recalc_date);
  if (recalc) return { dataDate: recalc, inferred: false };

  let latestActual: Date | null = null;
  for (const activity of activities) {
    for (const candidate of [activity.actualStart, activity.actualFinish]) {
      if (candidate && (!latestActual || candidate > latestActual)) {
        latestActual = candidate;
      }
    }
  }
  if (latestActual) return { dataDate: latestActual, inferred: true };

  const starts = activities.map((a) => a.start).filter((d): d is Date => !!d);
  if (starts.length) {
    return {
      dataDate: new Date(Math.min(...starts.map((d) => d.getTime()))),
      inferred: true,
    };
  }
  return { dataDate: null, inferred: true };
}

export async function parseXERFile(
  file: File,
  onProgress?: ParseProgress
): Promise<ScheduleModel> {
  onProgress?.(5, 'Reading file');
  const text = await file.text();

  onProgress?.(20, 'Splitting tables');
  const tables = readTables(text);

  onProgress?.(35, 'Reading calendars and codes');
  const calendars = buildCalendars(tables);
  const { byTask: activityCodesByTask, typeNames } = buildActivityCodes(tables);
  const assignmentsByTask = buildResourceAssignments(tables);

  const resources = new Map<string, ResourceInfo>();
  for (const row of tables['RSRC'] ?? []) {
    resources.set(row.rsrc_id, {
      id: row.rsrc_id,
      name: row.rsrc_name || row.rsrc_short_name || 'Resource',
      shortName: row.rsrc_short_name || '',
      type: row.rsrc_type?.replace('RT_', '') || 'Labour',
    });
  }

  const wbs: WbsNode[] = (tables['PROJWBS'] ?? []).map((row) => ({
    id: row.wbs_id,
    parentId: row.parent_wbs_id || null,
    name: row.wbs_name || row.wbs_short_name || 'WBS',
    code: row.wbs_short_name || '',
    sequence: num(row.seq_num),
    isProjectNode: row.proj_node_flag === 'Y',
  }));

  // The most-used calendar stands in wherever an activity has none.
  const calendarUsage = new Map<string, number>();
  for (const row of tables['TASK'] ?? []) {
    if (row.clndr_id) {
      calendarUsage.set(row.clndr_id, (calendarUsage.get(row.clndr_id) ?? 0) + 1);
    }
  }
  let defaultHoursPerDay = 8;
  if (calendarUsage.size) {
    const [dominant] = [...calendarUsage.entries()].sort((a, b) => b[1] - a[1])[0];
    defaultHoursPerDay = calendars.get(dominant)?.hoursPerDay ?? 8;
  }

  onProgress?.(55, 'Building activities');

  const relationships = buildRelationships(tables);
  const predecessorsByTask = new Map<string, Relationship[]>();
  const successorsByTask = new Map<string, Relationship[]>();
  for (const rel of relationships) {
    const preds = predecessorsByTask.get(rel.successorId);
    if (preds) preds.push(rel);
    else predecessorsByTask.set(rel.successorId, [rel]);

    const succs = successorsByTask.get(rel.predecessorId);
    if (succs) succs.push(rel);
    else successorsByTask.set(rel.predecessorId, [rel]);
  }

  const warnings: string[] = [];
  const taskRows = tables['TASK'] ?? [];
  if (taskRows.length === 0) {
    throw new Error('No TASK table found — this does not look like a P6 XER export.');
  }

  const activities: Activity[] = taskRows.map((row) => {
    const hoursPerDay = calendars.get(row.clndr_id)?.hoursPerDay ?? defaultHoursPerDay;
    const status = STATUS_MAP[row.status_code] ?? 'Not Started';
    const type = TASK_TYPE_MAP[row.task_type] ?? 'Other';

    const targetStart = parseXerDate(row.target_start_date);
    const targetFinish = parseXerDate(row.target_end_date);
    const actualStart = parseXerDate(row.act_start_date);
    const actualFinish = parseXerDate(row.act_end_date);

    const assignments = assignmentsByTask.get(row.task_id) ?? [];
    const budgetCost = assignments.reduce((sum, a) => sum + a.budgetCost, 0);
    const actualCost = assignments.reduce((sum, a) => sum + a.actualCost, 0);
    const remainingCost = assignments.reduce((sum, a) => sum + a.remainingCost, 0);

    const constraintType = CONSTRAINT_MAP[row.cstr_type] ?? null;
    const secondaryConstraintType = CONSTRAINT_MAP[row.cstr_type2] ?? null;
    const totalFloatDays = num(row.total_float_hr_cnt) / hoursPerDay;

    // P6 records phys_complete_pct only when the completion type is Physical;
    // for the other types fall back to the status so the field is never a lie.
    const physPct = num(row.phys_complete_pct, NaN);
    const percentComplete = Number.isFinite(physPct)
      ? physPct
      : status === 'Completed'
        ? 100
        : status === 'In Progress'
          ? 50
          : 0;

    return {
      id: row.task_id,
      code: row.task_code || row.task_id,
      name: row.task_name || 'Unnamed activity',
      wbsId: row.wbs_id || null,
      calendarId: row.clndr_id || null,
      type,
      isMilestone: type === 'Milestone' || type === 'Finish Milestone',

      status,
      isComplete: status === 'Completed',
      isInProgress: status === 'In Progress',
      isNotStarted: status === 'Not Started',

      targetStart,
      targetFinish,
      actualStart,
      actualFinish,
      earlyStart: parseXerDate(row.early_start_date),
      earlyFinish: parseXerDate(row.early_end_date),
      lateStart: parseXerDate(row.late_start_date),
      lateFinish: parseXerDate(row.late_end_date),

      start: actualStart ?? targetStart,
      finish: actualFinish ?? targetFinish,

      originalDurationDays: num(row.target_drtn_hr_cnt) / hoursPerDay,
      remainingDurationDays: num(row.remain_drtn_hr_cnt) / hoursPerDay,
      totalFloatDays,
      freeFloatDays: num(row.free_float_hr_cnt) / hoursPerDay,
      percentComplete,

      isCritical: totalFloatDays <= 0,
      isDrivingPath: row.driving_path_flag === 'Y',
      floatPath: row.float_path ? num(row.float_path) : null,

      constraintType,
      constraintDate: parseXerDate(row.cstr_date),
      secondaryConstraintType,
      hasHardConstraint:
        HARD_CONSTRAINTS.includes(constraintType) ||
        HARD_CONSTRAINTS.includes(secondaryConstraintType),

      predecessors: predecessorsByTask.get(row.task_id) ?? [],
      successors: successorsByTask.get(row.task_id) ?? [],
      resources: assignments,

      budgetCost,
      actualCost,
      remainingCost,
      atCompletionCost: actualCost + remainingCost,
      budgetUnits: assignments.reduce((sum, a) => sum + a.budgetUnits, 0),
      actualUnits: assignments.reduce((sum, a) => sum + a.actualUnits, 0),

      activityCodes: activityCodesByTask.get(row.task_id) ?? {},
      hoursPerDay,
      raw: row,
    };
  });

  onProgress?.(80, 'Resolving lags and dates');

  const activityById = new Map(activities.map((a) => [a.id, a]));

  // Lag is expressed on the successor's calendar.
  for (const rel of relationships) {
    const successor = activityById.get(rel.successorId);
    rel.lagDays = rel.lagHours / (successor?.hoursPerDay ?? defaultHoursPerDay);
  }

  const projectRow = (tables['PROJECT'] ?? []).find((r) => r.proj_id && r.task_id === undefined)
    ?? (tables['PROJECT'] ?? [])[0];

  const { dataDate, inferred } = resolveDataDate(projectRow, activities);
  if (inferred) {
    warnings.push(
      'No data date in the file — inferred from the latest actual date. Time-based metrics are approximate.'
    );
  }
  if (!tables['TASKRSRC']?.length) {
    warnings.push('No resource assignments (TASKRSRC) — cost and earned value analysis unavailable.');
  }
  if (!tables['PROJWBS']?.length) {
    warnings.push('No WBS (PROJWBS) in the export — WBS rollups unavailable.');
  }

  const starts = activities.map((a) => a.start).filter((d): d is Date => !!d);
  const finishes = activities.map((a) => a.finish).filter((d): d is Date => !!d);

  onProgress?.(100, 'Done');

  return {
    source: 'xer',
    fileName: file.name,
    projectId: projectRow?.proj_id ?? null,
    projectName:
      projectRow?.proj_short_name ||
      wbs.find((n) => n.isProjectNode)?.name ||
      file.name.replace(/\.xer$/i, ''),

    dataDate,
    dataDateInferred: inferred,
    plannedStart: parseXerDate(projectRow?.plan_start_date),
    scheduledFinish: parseXerDate(projectRow?.scd_end_date),

    start: starts.length ? new Date(Math.min(...starts.map((d) => d.getTime()))) : null,
    finish: finishes.length ? new Date(Math.max(...finishes.map((d) => d.getTime()))) : null,

    activities,
    activityById,
    relationships,
    wbs,
    wbsById: new Map(wbs.map((n) => [n.id, n])),
    resources,
    calendars,
    activityCodeTypes: typeNames,

    defaultHoursPerDay,
    warnings,
  };
}
